import { Dialog } from '@angular/cdk/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ButtonComponent } from '@kinomap-dev/ng-ui-lib/buttons';
import { ToolbarShellComponent } from 'app/shared/ui/toolbar-shell/toolbar-shell.component';
import { ConfirmModalComponent } from 'app/shared/ui/modals/confirm-modal/confirm-modal.component';
import { EmailRequiredModalComponent } from 'app/shared/ui/modals/email-required-modal/email-required-modal.component';
import { EmailSendModalComponent } from 'app/shared/ui/modals/email-send-modal/email-send-modal.component';
import { LoginService } from 'app/core/auth/login.service';
import { GoalApiService } from 'app/features/goal/data-access/goal-api.service';
import { GoalPageService } from 'app/features/goal/data-access/goal-page.service';
import { GoalStore } from 'app/features/goal/data-access/goal-store';

@Component({
  selector: 'app-toolbar-goal',
  imports: [ToolbarShellComponent, ButtonComponent],
  templateUrl: './toolbar-goal.component.html',
  styleUrl: './toolbar-goal.component.scss',
})
export class ToolbarGoalComponent {
  loginService: LoginService = inject(LoginService);
  dialog: Dialog = inject(Dialog);
  private readonly goalApi: GoalApiService = inject(GoalApiService);
  private readonly goalPage: GoalPageService = inject(GoalPageService);
  private goalStore: GoalStore = inject(GoalStore);

  readonly resetLoading: WritableSignal<boolean> = signal<boolean>(false);

  readonly canManageGoal: Signal<boolean> = computed<boolean>(
    () =>
      this.loginService.isAdmin() ||
      this.loginService.username() === this.goalStore.context()?.username
  );

  openResetConfirmModal(): void {
    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmation',
        message: 'Êtes-vous sûr de vouloir réinitialiser les données du leaderboard ?',
        confirmText: 'Confirmer',
        cancelText: 'Annuler',
        onConfirm: () => void this.reset(),
      },
    });
  }

  async reset(): Promise<void> {
    const context = this.goalStore.context();
    if (!context) return;

    this.resetLoading.set(true);
    try {
      await firstValueFrom(this.goalApi.resetGoal(context.username));
      this.goalPage.refreshNow();
    } catch (error: unknown) {
      console.error(error);
    } finally {
      this.resetLoading.set(false);
    }
  }

  openRequiredEmailModal(): void {
    const context = this.goalStore.context();
    if (!context) return;

    this.dialog.open(EmailRequiredModalComponent, {
      data: {
        onConfirm: (email: string) => void this.export(context.username, email),
      },
    });
  }

  async export(username: string, email: string): Promise<void> {
    try {
      await firstValueFrom(this.goalApi.requestExport(username, email));
      this.openSendEmailModal();
    } catch (error: unknown) {
      if (error instanceof HttpErrorResponse && error.status === 200) {
        this.openSendEmailModal();
      } else {
        console.error('Error exporting data:', error);
      }
    }
  }

  openSendEmailModal(): void {
    this.dialog.open(EmailSendModalComponent);
  }

  openLogoutModal(): void {
    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmation',
        message: 'Êtes-vous sûr de vouloir vous déconnecter ?',
        confirmText: 'Confirmer',
        cancelText: 'Annuler',
        onConfirm: () => this.loginService.logout(),
      },
    });
  }
}
